import { useState, useCallback, HTMLAttributes, type ReactNode } from 'react';
import classnames from 'classnames';
import { Close } from 'assets/icons';
import { Col, Grid, Row } from 'react-flexbox-grid';
import s from './Alert.module.scss';

interface IAlertProps extends HTMLAttributes<HTMLDivElement> {
  color?: string;
  icon?: ReactNode;
  /** The localStorage key that remembers a closed alert. */
  id?: string;
  isClosable?: boolean;
  withContainer?: boolean;
}

const colorToClassNameMap: Record<string, string> = {
  primary: s.primary,
  danger: s.danger,
  warning: s.warning,
  secondary: s.secondary,
};

const Alert = ({
  children,
  color = 'primary',
  icon,
  className,
  id,
  isClosable = false,
  withContainer = false,
  ...props
}: IAlertProps) => {
  // localStorage turns the key and the value into strings (`getItem(undefined)` reads "undefined",
  // `JSON.parse(null)` parses "null").
  const visible: unknown = JSON.parse(String(window?.localStorage?.getItem(String(id))));
  const [isVisible, setIsVisible] = useState(typeof visible === 'boolean' ? visible : true);

  const handleOnCloseBtnClick = useCallback(() => {
    setIsVisible(false);
    window?.localStorage?.setItem(String(id), String(false));
  }, [id]);

  const AlertContent = () =>
    isVisible ? (
      <div className={classnames(s.alert, colorToClassNameMap[color], className)} {...props}>
        {children}
        {isClosable ? <Close className={s.closeBtn} onClick={handleOnCloseBtnClick} /> : null}
        {icon ? <div className={s.iconContainer}>{icon}</div> : null}
      </div>
    ) : null;

  const AlertWithContainer = () => (
    <Grid>
      <Row>
        <Col xs={12}>
          <AlertContent />
        </Col>
      </Row>
    </Grid>
  );

  return withContainer ? <AlertWithContainer /> : <AlertContent />;
};

export default Alert;
